import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {
  Collection,
  CollectionReport,
  CollectionVariant,
  DerinOrganization,
  SurveyStatus,
  addCollectionVariant,
  createCollection,
  createCollectionFromConfig,
  deleteCollection,
  deleteCollectionAbout,
  deleteCollectionReport,
  getCollectionConfig,
  getCollectionDetail,
  getCollectionsByOrganization,
  removeCollectionVariant,
  setPrimaryVariant,
  setCollectionEarlyAccess,
  updateVariantLabel,
  uploadCollectionAbout,
  uploadCollectionConfig,
  uploadCollectionReports,
  updateSurveyStatus,
  uploadVariantVersion,
} from "../../../api/derin";
import { useToast } from "../Toast";
import { errorMessageOf } from "../errorMessage";
import CollectionCard from "./CollectionCard";
import CreateCollectionModal from "./CreateCollectionModal";
import CreateFromConfigModal from "./CreateFromConfigModal";
import AddVariantModal from "./AddVariantModal";
import UploadVersionModal from "./UploadVersionModal";
import ConfigFileModal from "./ConfigFileModal";
import AboutStudyModal from "./AboutStudyModal";
import ReportsModal from "./ReportsModal";
import RemoveReportDialog from "./RemoveReportDialog";
import RemoveAboutDialog from "./RemoveAboutDialog";
import RemoveVariantDialog from "./RemoveVariantDialog";
import DeactivateCollectionDialog from "./DeactivateCollectionDialog";
import DeleteCollectionDialog from "./DeleteCollectionDialog";
import { CreateCollectionDraft } from "./types";
import {
  collectionColors,
  font,
  outlinedButtonSx,
  primaryButtonSx,
  subtitleSx,
} from "./tokens";

interface CollectionsTabProps {
  organization: DerinOrganization;
  /** Null until the first load finishes, so callers don't show a count of 0. */
  onCountChange?: (count: number | null) => void;
}

const CollectionsTab: React.FC<CollectionsTabProps> = ({
  organization,
  onCountChange,
}) => {
  const { showSuccess, showError } = useToast();

  const [collections, setCollections] = useState<Collection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [expandedCollectionId, setExpandedCollectionId] = useState<string | null>(null);
  // Details of cards that have been opened. The listing carries only what a closed card
  // shows, so this is where the variants, file names and reports live once fetched.
  const [details, setDetails] = useState<Record<string, Collection>>({});
  const [loadingDetailFor, setLoadingDetailFor] = useState<string | null>(null);
  // Keyed by collection so a failure belongs to the card it happened on, not the tab.
  const [detailErrors, setDetailErrors] = useState<Record<string, string>>({});
  const [openHistoryFor, setOpenHistoryFor] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createFromConfigOpen, setCreateFromConfigOpen] = useState(false);
  const [addVariantFor, setAddVariantFor] = useState<Collection | null>(null);
  const [configFor, setConfigFor] = useState<Collection | null>(null);
  const [aboutFor, setAboutFor] = useState<Collection | null>(null);
  const [reportsFor, setReportsFor] = useState<Collection | null>(null);
  const [uploadVersionFor, setUploadVersionFor] = useState<
    { collection: Collection; variant: CollectionVariant } | null
  >(null);
  const [removeVariantFor, setRemoveVariantFor] = useState<
    { collection: Collection; variant: CollectionVariant } | null
  >(null);
  const [removeReportFor, setRemoveReportFor] = useState<
    { collection: Collection; report: CollectionReport } | null
  >(null);
  const [removeAboutFor, setRemoveAboutFor] = useState<Collection | null>(null);
  const [deactivateFor, setDeactivateFor] = useState<Collection | null>(null);
  // The collection queued for deletion, resolved to full detail so the dialog can show
  // the variant count and whether any variant has responses.
  const [deleteFor, setDeleteFor] = useState<Collection | null>(null);
  const [deleteLoadingDetail, setDeleteLoadingDetail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Collection status. The listing carries `status` (mirrored from the primary
  // variant); statusOverride keeps the optimistic result of a toggle so the card
  // updates without refetching the whole tab — same shape as the Surveys tab.
  const [statusOverride, setStatusOverride] = useState<Record<string, SurveyStatus>>({});
  const [togglingCollectionId, setTogglingCollectionId] = useState<string | null>(null);
  const isCollectionActive = (collection: Collection) =>
    (statusOverride[collection.id] ?? collection.status) === "running";

  // Early access, kept the same way and for the same reason: a switch that waits for a
  // listing refetch before it moves reads as a switch that did not take.
  const [earlyAccessOverride, setEarlyAccessOverride] = useState<
    Record<string, boolean>
  >({});
  const [togglingEarlyAccessFor, setTogglingEarlyAccessFor] = useState<
    string | null
  >(null);
  const isEarlyAccessEnabled = (collection: Collection) =>
    earlyAccessOverride[collection.id] ?? collection.earlyAccessEnabled;

  /**
   * Active/Inactive for a whole collection. It calls the same status endpoint the
   * Surveys tab does, with the collection id in place of the survey id: the API fans
   * the change out to every variant and, on a close, asks for one theme-analysis run
   * for the collection rather than one per variant.
   */
  const applyStatus = async (collection: Collection, next: SurveyStatus) => {
    setTogglingCollectionId(collection.id);
    try {
      const res = await updateSurveyStatus(collection.id, organization.id, next);
      setStatusOverride((prev) => ({ ...prev, [collection.id]: res.status }));
      showSuccess(
        next === "running"
          ? "Survey activated successfully"
          : "Survey deactivated successfully"
      );
      setDeactivateFor(null);
    } catch (err) {
      console.error("Error updating collection status:", err);
      showError(`Error updating collection status: ${errorMessageOf(err)}`);
    } finally {
      setTogglingCollectionId(null);
    }
  };

  /**
   * Deactivating asks first. It closes every variant to new respondents and submits what
   * has been collected for processing, which is more than one mis-click should be able to
   * do. Activating goes straight through — it is the reversible direction.
   */
  const handleToggleStatus = (collection: Collection) => {
    if (isCollectionActive(collection)) {
      setDeactivateFor(collection);
      return;
    }
    void applyStatus(collection, "running");
  };

  /**
   * Early access to Results and Chat with Data, per collection.
   *
   * Unlike the other card actions this does not run through `runMutation`: refetching
   * the listing for a boolean would drop the opened-card cache and make the accordion
   * rebuild itself under the switch. The optimistic value is enough, and it is reverted
   * if the write fails.
   */
  const handleToggleEarlyAccess = async (collection: Collection) => {
    const next = !isEarlyAccessEnabled(collection);
    setTogglingEarlyAccessFor(collection.id);
    setEarlyAccessOverride((prev) => ({ ...prev, [collection.id]: next }));
    try {
      await setCollectionEarlyAccess(collection.id, organization.id, next);
      showSuccess(
        next
          ? "Early access enabled — clients can now see Results and Derin AI"
          : "Early access disabled"
      );
    } catch (err) {
      console.error("Error updating early access:", err);
      showError(`Error updating early access: ${errorMessageOf(err)}`);
      // Back to what the server last told us, rather than leaving the switch showing a
      // change that never landed.
      setEarlyAccessOverride((prev) => ({
        ...prev,
        [collection.id]: !next,
      }));
    } finally {
      setTogglingEarlyAccessFor(null);
    }
  };

  /**
   * Opens a card, fetching what the listing left out the first time it is opened.
   *
   * Cached per collection: reopening the same card is free. The cache is dropped whenever
   * the listing is refetched, which is what every mutation does.
   */
  /**
   * Fetches what the listing left out. Kept separate from the toggle so the card's own
   * retry can call it again without reopening.
   */
  const loadDetail = useCallback(
    async (collection: Collection) => {
      setLoadingDetailFor(collection.id);
      setDetailErrors((prev) => {
        const { [collection.id]: _dropped, ...rest } = prev;
        return rest;
      });
      try {
        const detail = await getCollectionDetail(collection.id, organization.id);
        setDetails((prev) => ({ ...prev, [collection.id]: detail }));
      } catch (err) {
        console.error("Error fetching collection detail:", err);
        // Recorded against the card and shown inside it, rather than closing the card and
        // firing a toast: closing throws away what the listing already gave us and leaves
        // no way to try again except reopening, which reads as the card refusing to open.
        setDetailErrors((prev) => ({
          ...prev,
          [collection.id]: errorMessageOf(err),
        }));
      } finally {
        setLoadingDetailFor(null);
      }
    },
    [organization.id]
  );

  /** Opening and closing only moves the marker; the effect below does the fetching. */
  const handleToggleExpand = (collection: Collection) => {
    setExpandedCollectionId((prev) =>
      prev === collection.id ? null : collection.id
    );
  };

  const fetchCollections = useCallback(async () => {
    setIsLoading(true);
    try {
      setCollections(await getCollectionsByOrganization(organization.id));
      // Every mutation refetches through here, so this is the one place that has to drop
      // the opened-card cache. Keeping it would show a stale label after a rename.
      setDetails({});
      setDetailErrors({});
      // The rows just fetched carry `earlyAccessEnabled`, so the optimistic values have
      // nothing left to add — and holding them would mask a change made elsewhere.
      setEarlyAccessOverride({});
    } catch (err) {
      console.error("Error fetching collections:", err);
      showError(`Error fetching collections: ${errorMessageOf(err)}`);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [organization.id, showError]);

  useEffect(() => {
    void fetchCollections();
  }, [fetchCollections]);

  /**
   * The open card always ends up with its detail, whoever opened it.
   *
   * Two paths need this. Creating a collection expands the new card by setting the id
   * directly, and every mutation refetches the listing, which drops the detail cache —
   * in both cases the card would fall back to its listing row, whose `variants` is empty
   * by design, and read as "No variants in this collection yet" until it was closed and
   * reopened. An error is left alone so a failed fetch cannot loop; the card's own retry
   * is what clears it.
   */
  useEffect(() => {
    if (!expandedCollectionId) return;
    if (details[expandedCollectionId] || detailErrors[expandedCollectionId]) return;
    if (loadingDetailFor === expandedCollectionId) return;
    const open = collections.find((item) => item.id === expandedCollectionId);
    if (open) void loadDetail(open);
  }, [
    expandedCollectionId,
    collections,
    details,
    detailErrors,
    loadingDetailFor,
    loadDetail,
  ]);

  useEffect(() => {
    onCountChange?.(hasLoaded ? collections.length : null);
  }, [collections.length, hasLoaded, onCountChange]);

  /**
   * Every mutation ends the same way: report it, close whatever modal drove it and
   * refetch, so the tab always shows server state rather than a guess about it.
   */
  const runMutation = async (
    action: () => Promise<unknown>,
    successMessage: string,
    onDone?: () => void
  ) => {
    setIsSubmitting(true);
    try {
      await action();
      showSuccess(successMessage);
      onDone?.();
      await fetchCollections();
    } catch (err) {
      console.error(`${successMessage} failed:`, err);
      showError(errorMessageOf(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Open the delete confirmation. Delete is available from the closed card, whose
   * listing carries no variant rows, so the full detail (needed to warn about responses)
   * is resolved here. The dialog opens immediately with a spinner rather than awaiting
   * silently; the re-entry guard means a second menu click during the fetch is a no-op
   * instead of a duplicate request.
   */
  const handleDeleteClick = async (collection: Collection) => {
    if (deleteFor || deleteLoadingDetail) return;
    const cached = details[collection.id];
    if (cached) {
      setDeleteFor(cached);
      return;
    }
    // Open on the listing row so the dialog is up at once; swap in the detail when it lands.
    setDeleteFor(collection);
    setDeleteLoadingDetail(true);
    try {
      const detail = await getCollectionDetail(collection.id, organization.id);
      setDetails((prev) => ({ ...prev, [collection.id]: detail }));
      setDeleteFor(detail);
    } catch (err) {
      console.error("Error loading collection for delete:", err);
      showError(errorMessageOf(err));
      setDeleteFor(null);
    } finally {
      setDeleteLoadingDetail(false);
    }
  };

  const handleConfirmDelete = () => {
    if (!deleteFor) return;
    void runMutation(
      () => deleteCollection(deleteFor.id, organization.id),
      "Collection deleted",
      () => setDeleteFor(null)
    );
  };

  // Reading the files happens inside runMutation, not before it: an unreadable file would
  // otherwise reject outside the try and leave the modal stuck with no message.
  const handleCreateCollection = (draft: CreateCollectionDraft) =>
    runMutation(
      async () => {
        const [primaryContent, ...additionalContents] = await Promise.all([
          draft.primary.file.text(),
          ...draft.additional.map((variant) => variant.file.text()),
        ]);
        const result = await createCollection({
          name: draft.name,
          description: draft.description,
          organization_id: organization.id,
          primary_variant: {
            label: draft.primary.label,
            survey_content: primaryContent,
          },
          additional_variants: draft.additional.map((variant, index) => ({
            label: variant.label,
            survey_content: additionalContents[index],
          })),
        });
        // The collection and its primary exist; only an extra variant failed. Surfaced
        // as a warning so the researcher knows to retry just that one.
        if (result.failedVariant) {
          showError(
            `Variant "${result.failedVariant.label}" failed: ${result.failedVariant.error}`
          );
        }
        setExpandedCollectionId(result.collectionId);
      },
      "Collection created successfully",
      () => setCreateOpen(false)
    );

  /**
   * The other way in: a configuration file naming surveys that already exist. Everything
   * the collection needs is in the file, so there is nothing to collect from the modal.
   */
  const handleCreateFromConfig = (file: File) =>
    runMutation(
      async () => {
        const collection = await createCollectionFromConfig(
          organization.id,
          await file.text(),
          file.name
        );
        setExpandedCollectionId(collection.id);
      },
      "Collection created successfully",
      () => setCreateFromConfigOpen(false)
    );

  const handleAddVariant = (
    collection: Collection,
    payload: { label: string; file: File }
  ) =>
    runMutation(
      async () =>
        addCollectionVariant(collection.id, organization.id, {
          label: payload.label,
          survey_content: await payload.file.text(),
        }),
      "Variant added successfully",
      () => setAddVariantFor(null)
    );

  const handleUploadVersion = (
    collection: Collection,
    variant: CollectionVariant,
    file: File
  ) =>
    runMutation(
      async () =>
        uploadVariantVersion(
          collection.id,
          organization.id,
          variant.surveyOriginalId,
          await file.text()
        ),
      "New version uploaded successfully",
      () => setUploadVersionFor(null)
    );

  const handleRenameVariant = (
    collection: Collection,
    variant: CollectionVariant,
    label: string
  ) =>
    runMutation(
      () =>
        updateVariantLabel(
          collection.id,
          organization.id,
          variant.surveyOriginalId,
          label
        ),
      "Variant label updated"
    );

  const handleRemoveVariant = (
    collection: Collection,
    variant: CollectionVariant
  ) =>
    runMutation(
      () =>
        removeCollectionVariant(
          collection.id,
          organization.id,
          variant.surveyOriginalId
        ),
      "Variant removed from collection",
      () => setRemoveVariantFor(null)
    );

  const handleUploadConfigFile = (collection: Collection, file: File) =>
    runMutation(
      async () =>
        uploadCollectionConfig(
          collection.id,
          organization.id,
          await file.text(),
          file.name
        ),
      "Configuration file applied",
      () => setConfigFor(null)
    );

  const handleSetPrimaryVariant = (
    collection: Collection,
    variant: CollectionVariant
  ) =>
    runMutation(
      () =>
        setPrimaryVariant(
          collection.id,
          organization.id,
          variant.surveyOriginalId
        ),
      `"${variant.label}" is now the primary variant`
    );

  /** Downloads the applied configuration. A read, so it works without the admin key. */
  /**
   * The study write-up the client app renders. The API parses the YAML before storing
   * it, so a file that cannot be rendered is refused here, with the reason.
   */
  const handleUploadAboutFile = (collection: Collection, file: File) =>
    runMutation(
      async () =>
        uploadCollectionAbout(
          collection.id,
          organization.id,
          await file.text(),
          file.name
        ),
      "About the Study file applied",
      () => setAboutFor(null)
    );

  /**
   * The deliverables the client downloads. Multipart, so unlike every other upload here
   * the file is handed over as-is rather than read into a string first.
   */
  const handleUploadReports = (
    collection: Collection,
    reports: { file: File; title: string }[]
  ) =>
    runMutation(
      () => uploadCollectionReports(collection.id, organization.id, reports),
      reports.length === 1
        ? "Report uploaded successfully"
        : `${reports.length} reports uploaded successfully`,
      () => setReportsFor(null)
    );

  const handleRemoveAbout = (collection: Collection) =>
    runMutation(
      () => deleteCollectionAbout(collection.id, organization.id),
      "About the Study file removed",
      () => setRemoveAboutFor(null)
    );

  const handleRemoveReport = (collection: Collection, report: CollectionReport) =>
    runMutation(
      () => deleteCollectionReport(collection.id, organization.id, report.id),
      "Report removed",
      () => setRemoveReportFor(null)
    );

  const handleDownloadConfig = async (collection: Collection) => {
    try {
      const { configYaml } = await getCollectionConfig(
        collection.id,
        organization.id
      );
      const url = URL.createObjectURL(
        new Blob([configYaml], { type: "text/yaml" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = collection.configFileName ?? "config.yaml";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading configuration:", err);
      showError(`Error downloading configuration: ${errorMessageOf(err)}`);
    }
  };

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        width: "100%",
        background: collectionColors.white,
        border: `1px solid ${collectionColors.border}`,
        borderRadius: "14px",
      }}
    >
      {/* Card header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          padding: "0px 24px",
          height: "80px",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
          <Typography
            sx={{
              fontFamily: font,
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: "27px",
              color: collectionColors.textPrimary,
            }}
          >
            Collections
          </Typography>
          <Typography sx={{ ...subtitleSx, fontSize: "16px", lineHeight: "24px" }}>
            Manage survey file collections for {organization.name}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Button
            startIcon={<DescriptionOutlinedIcon sx={{ width: 16, height: 16 }} />}
            onClick={() => setCreateFromConfigOpen(true)}
            disabled={isSubmitting}
            sx={outlinedButtonSx}
          >
            From Config File
          </Button>

          <Button
            startIcon={<AddIcon sx={{ width: 16, height: 16 }} />}
            onClick={() => setCreateOpen(true)}
            disabled={isSubmitting}
            sx={primaryButtonSx}
          >
            New Collection
          </Button>
        </Box>
      </Box>

      {/* Collection list */}
      <Box sx={{ display: "flex", flexDirection: "column", padding: "0px 25px 25px", gap: "12px" }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : collections.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Typography sx={subtitleSx}>No collections found</Typography>
          </Box>
        ) : (
          collections.map((collection) => (
            <CollectionCard
              key={collection.id}
              collection={details[collection.id] ?? collection}
              isLoadingDetail={loadingDetailFor === collection.id}
              detailError={detailErrors[collection.id] ?? null}
              onRetryDetail={() => void loadDetail(collection)}
              expanded={expandedCollectionId === collection.id}
              isActive={isCollectionActive(collection)}
              isTogglingStatus={togglingCollectionId === collection.id}
              onToggleStatus={() => handleToggleStatus(collection)}
              onToggleExpand={() => handleToggleExpand(collection)}
              openHistoryFor={openHistoryFor}
              onToggleVariantHistory={(surveyOriginalId) =>
                setOpenHistoryFor((prev) =>
                  prev === surveyOriginalId ? null : surveyOriginalId
                )
              }
              onAddVariant={() => setAddVariantFor(collection)}
              onUploadConfigFile={() => setConfigFor(collection)}
              onUploadAboutFile={() => setAboutFor(collection)}
              onRemoveAboutFile={() => setRemoveAboutFor(collection)}
              onUploadReports={() => setReportsFor(collection)}
              onRemoveReport={(report) => setRemoveReportFor({ collection, report })}
              onRenameVariant={(variant, label) =>
                void handleRenameVariant(collection, variant, label)
              }
              onUploadVersion={(variant) => setUploadVersionFor({ collection, variant })}
              onSetPrimaryVariant={(variant) =>
                void handleSetPrimaryVariant(collection, variant)
              }
              onRemoveVariant={(variant) => setRemoveVariantFor({ collection, variant })}
              onDownloadConfig={() => void handleDownloadConfig(collection)}
              onDelete={() => void handleDeleteClick(collection)}
              earlyAccessEnabled={isEarlyAccessEnabled(collection)}
              isTogglingEarlyAccess={togglingEarlyAccessFor === collection.id}
              onToggleEarlyAccess={() => void handleToggleEarlyAccess(collection)}
            />
          ))
        )}
      </Box>

      <CreateCollectionModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(draft) => void handleCreateCollection(draft)}
        isSubmitting={isSubmitting}
      />

      <CreateFromConfigModal
        open={createFromConfigOpen}
        onClose={() => setCreateFromConfigOpen(false)}
        onSubmit={(file) => void handleCreateFromConfig(file)}
        isSubmitting={isSubmitting}
      />

      <AddVariantModal
        open={Boolean(addVariantFor)}
        collection={addVariantFor}
        onClose={() => setAddVariantFor(null)}
        onSubmit={(payload) =>
          addVariantFor && void handleAddVariant(addVariantFor, payload)
        }
        isSubmitting={isSubmitting}
      />

      <UploadVersionModal
        open={Boolean(uploadVersionFor)}
        variant={uploadVersionFor?.variant ?? null}
        onClose={() => setUploadVersionFor(null)}
        onSubmit={(file) =>
          uploadVersionFor &&
          void handleUploadVersion(
            uploadVersionFor.collection,
            uploadVersionFor.variant,
            file
          )
        }
        isSubmitting={isSubmitting}
      />

      <ConfigFileModal
        open={Boolean(configFor)}
        collection={configFor}
        onClose={() => setConfigFor(null)}
        onSubmit={(file) => configFor && void handleUploadConfigFile(configFor, file)}
        isSubmitting={isSubmitting}
      />

      <AboutStudyModal
        open={Boolean(aboutFor)}
        collection={aboutFor}
        onClose={() => setAboutFor(null)}
        onSubmit={(file) => aboutFor && void handleUploadAboutFile(aboutFor, file)}
        isSubmitting={isSubmitting}
      />

      <ReportsModal
        open={Boolean(reportsFor)}
        collection={reportsFor}
        onClose={() => setReportsFor(null)}
        onSubmit={(reports) => reportsFor && void handleUploadReports(reportsFor, reports)}
        isSubmitting={isSubmitting}
      />

      <RemoveVariantDialog
        open={Boolean(removeVariantFor)}
        variant={removeVariantFor?.variant ?? null}
        onClose={() => setRemoveVariantFor(null)}
        onConfirm={() =>
          removeVariantFor &&
          void handleRemoveVariant(removeVariantFor.collection, removeVariantFor.variant)
        }
        isSubmitting={isSubmitting}
      />

      <RemoveAboutDialog
        open={Boolean(removeAboutFor)}
        collection={removeAboutFor}
        onClose={() => setRemoveAboutFor(null)}
        onConfirm={() => removeAboutFor && void handleRemoveAbout(removeAboutFor)}
        isSubmitting={isSubmitting}
      />

      <RemoveReportDialog
        open={Boolean(removeReportFor)}
        report={removeReportFor?.report ?? null}
        onClose={() => setRemoveReportFor(null)}
        onConfirm={() =>
          removeReportFor &&
          void handleRemoveReport(removeReportFor.collection, removeReportFor.report)
        }
        isSubmitting={isSubmitting}
      />

      {/* Driven by togglingCollectionId, not isSubmitting: the status change goes through
          applyStatus rather than runMutation, since it updates one card optimistically
          instead of refetching the tab. */}
      <DeactivateCollectionDialog
        open={Boolean(deactivateFor)}
        collection={deactivateFor}
        onClose={() => setDeactivateFor(null)}
        onConfirm={() => deactivateFor && void applyStatus(deactivateFor, "stopped")}
        isSubmitting={togglingCollectionId === deactivateFor?.id}
      />

      <DeleteCollectionDialog
        open={Boolean(deleteFor)}
        collection={deleteFor}
        hasResponses={Boolean(
          deleteFor?.variants?.some((variant) => variant.transcriptCount > 0)
        )}
        isActive={Boolean(deleteFor?.isActive)}
        loadingDetail={deleteLoadingDetail}
        onClose={() => {
          setDeleteFor(null);
          setDeleteLoadingDetail(false);
        }}
        onConfirm={handleConfirmDelete}
        isSubmitting={isSubmitting}
      />
    </Box>
  );
};

export default CollectionsTab;
